import { useEffect, useRef, useState, useCallback } from 'react';

// The vendored TS-compiled engine assigns the class to both `window.PdfTeXEngine`
// (top-level var) and `window.exports.PdfTeXEngine` — read either, defensively.
function getEngineCtor() {
  return window.PdfTeXEngine || (window.exports && window.exports.PdfTeXEngine) || null;
}

// Loads the vendored engine script once and resolves the engine constructor.
function loadEngineScript() {
  const existing = getEngineCtor();
  if (existing) return Promise.resolve(existing);
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `${process.env.PUBLIC_URL || ''}/swiftlatex/PdfTeXEngine.js`;
    s.onload = () => {
      const ctor = getEngineCtor();
      if (ctor) resolve(ctor);
      else reject(new Error('LaTeX engine loaded but constructor not found'));
    };
    s.onerror = () => reject(new Error('Failed to load LaTeX engine'));
    document.body.appendChild(s);
  });
}

export function useLatexEngine({ enabled = true } = {}) {
  const engineRef = useRef(null);
  const chainRef = useRef(Promise.resolve());
  const [ready, setReady] = useState(false);
  const [compiling, setCompiling] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState(null);
  const [initNonce, setInitNonce] = useState(0);

  useEffect(() => {
    if (!enabled || engineRef.current) return undefined;
    let cancelled = false;
    setError(null);
    setInitializing(true);
    (async () => {
      try {
        const PdfTeXEngine = await loadEngineScript();
        const engine = new PdfTeXEngine();
        await engine.loadEngine();
        // Route TeX Live fetches through our proxy: immutable-cached on the
        // browser AND file-cached on our server (api/texlive.php).
        if (typeof engine.setTexliveEndpoint === 'function') {
          engine.setTexliveEndpoint(`${process.env.REACT_APP_API_URL}/api/texlive.php?path=`);
        }
        if (cancelled) {
          if (typeof engine.closeWorker === 'function') engine.closeWorker();
          return;
        }
        engineRef.current = engine;
        setReady(true);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Engine failed to load');
      } finally {
        if (!cancelled) setInitializing(false);
      }
    })();
    return () => { cancelled = true; setInitializing(false); };
  }, [enabled, initNonce]);

  const retryInit = useCallback(() => {
    engineRef.current = null;
    setInitNonce(n => n + 1);
  }, []);

  // Compiles are serialized via a promise chain: each waits for the previous to
  // settle, so we never call compileLaTeX() while the engine worker is Busy
  // (which throws "Engine is still spinning").
  const compile = useCallback((tex) => {
    const run = chainRef.current.then(async () => {
      const engine = engineRef.current;
      if (!engine) return { status: -1, pdf: null, log: 'Engine not ready' };
      setCompiling(true);
      try {
        engine.writeMemFSFile('main.tex', tex);
        engine.setEngineMainFile('main.tex');
        const r = await engine.compileLaTeX();
        const pdf = r.pdf ? new Uint8Array(r.pdf) : null;
        return { status: r.status, pdf, log: r.log || '' };
      } finally {
        setCompiling(false);
      }
    });
    // Keep the chain alive even if this run rejects.
    chainRef.current = run.catch(() => {});
    return run;
  }, []);

  return { compile, ready, compiling, initializing, error, retryInit };
}
